import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { CenteredLoader } from '@/components_deprecated'
import { useUserChecklists } from '@/modules/checklists/hooks'
import type { ActiveModuleName } from '@/modules/companies/CompanySettings/hooks'
import {
  useCompositeModules,
  useModuleConfigs,
} from '@/modules/companies/CompanySettings/hooks'
import { IS_LOGGED_IN_QUERY } from '@/modules/login/queries'
import React from 'react'
import { Navigate, useLocation } from 'react-router'
import { usePermissions, useUser } from '@/utils/hooks'

interface AuthRequiredProps {
  permissions?: string | string[]
  disallowAdmin?: boolean
  module?: ActiveModuleName | 'main'
  redirectTo?: string
}

export const AuthRequired: React.JSX.CFC<AuthRequiredProps> = ({
  children,
  permissions,
  disallowAdmin = false,
  module,
  redirectTo = '/login',
}) => {
  const translations = useTranslate({
    notPermitted: 'error.not-permitted-to-view',
  })

  const location = useLocation()

  const user = useUser()
  const { hasPermissions } = usePermissions()
  const { hasUnresolvedChecklists } = useUserChecklists(user, undefined, true)

  const { loading, data, error } = useQuery(IS_LOGGED_IN_QUERY, {
    pollInterval: 60000,
  })

  const modules = useModuleConfigs(typeof module === 'undefined')
  const { mainModuleActive } = useCompositeModules()

  // Wait for the modules to load if we are checking for a specific module
  if (typeof module !== 'undefined' && modules.loading)
    return <CenteredLoader />

  // Wait for the login query to load
  if (loading || !data) return <CenteredLoader />

  // Check if the user has access to the specified module
  const hasModuleAccess =
    typeof module === 'undefined' ||
    (module === 'main' ? mainModuleActive : !!modules.active[module])

  if (!hasModuleAccess) return <Navigate to="/dashboard" />

  // Check if the user has unresolved checklists
  if (
    hasUnresolvedChecklists &&
    !location.pathname.includes('mandatory-checklists')
  )
    return <Navigate to="/mandatory-checklists" />

  // Check if the user has the required permissions
  if (!!permissions?.length && !hasPermissions(permissions, !disallowAdmin))
    return <p>{translations.notPermitted}</p>

  // Check if the user is logged in
  if (!data.isLoggedIn || !!error) return <Navigate to={redirectTo} />

  return <>{children}</>
}
